import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LayoutDashboard, FileText, BarChart3, Shield, LogOut, User, Building2, ChevronDown, AlertCircle, Info } from 'lucide-react';
import api from '../lib/api';
import SEO from '../components/SEO';
import ClientDocuments from '../components/ClientDocuments';

function CompanySelector({ companies, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  if (companies.length === 0) return null;
  const current = companies.find(c => c.id === selected);

  return (
    <div className="relative" data-testid="company-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-lg px-4 py-2 transition-colors text-left"
      >
        <Building2 size={16} className="text-[#C5A059]" />
        <span className="text-white text-sm font-medium truncate max-w-[200px]">
          {current?.company_name || 'Válasszon céget'}
        </span>
        <ChevronDown size={14} className={`text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-[#E2E8F0] min-w-[240px] z-50 py-1 animate-fade-in">
          {companies.map(co => (
            <button
              key={co.id}
              data-testid={`select-company-${co.id}`}
              onClick={() => { onSelect(co.id); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                co.id === selected ? 'bg-[#0B1B3D]/5 text-[#0B1B3D] font-semibold' : 'text-[#64748B] hover:bg-[#FAFAFA] hover:text-[#0B1B3D]'
              }`}
            >
              <span className="block">{co.company_name}</span>
              {co.novitax_enabled && <span className="text-xs text-[#C5A059]">Novitax aktív</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NovitaxNotice({ message, type = 'info' }) {
  const Icon = type === 'error' ? AlertCircle : Info;
  const colors = type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-[#0B1B3D]/5 text-[#64748B] border-[#E2E8F0]';
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${colors}`}>
      <Icon size={18} className="mt-0.5 flex-shrink-0" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/portal/dashboard').then(r => {
      setDashData(r.data);
      const companies = r.data.companies || [];
      if (companies.length === 1) {
        setSelectedCompanyId(companies[0].id);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Load selected company detail
  useEffect(() => {
    if (!selectedCompanyId) { setCompanyDetail(null); return; }
    api.get(`/portal/company/${selectedCompanyId}`)
      .then(r => setCompanyDetail(r.data))
      .catch(() => setCompanyDetail(null));
  }, [selectedCompanyId]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) return null;
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const companies = dashData?.companies || [];
  const hasCompanies = companies.length > 0;

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#FAFAFA]">
      <SEO title="Ügyfélportál" description="AGM Consulting ügyfélportál" />

      {/* Header */}
      <div className="bg-[#0B1B3D] py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Ügyfélportál</span>
              <h1 className="mt-2 text-2xl sm:text-3xl font-['Outfit'] font-semibold text-white">
                Üdvözöljük, {user.name || user.email}!
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {companies.length > 1 && (
                <CompanySelector
                  companies={companies}
                  selected={selectedCompanyId}
                  onSelect={setSelectedCompanyId}
                />
              )}
              <Button
                data-testid="dashboard-logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                <LogOut size={16} className="mr-2" /> Kijelentkezés
              </Button>
            </div>
          </div>
          {/* Single company name badge */}
          {companies.length === 1 && (
            <div className="mt-3 flex items-center gap-2">
              <Building2 size={14} className="text-[#C5A059]" />
              <span className="text-[#94A3B8] text-sm">{companies[0].company_name}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* No companies assigned */}
        {!hasCompanies && (
          <NovitaxNotice
            message="Jelenleg nincs cég hozzárendelve a fiókjához. Kérjük vegye fel a kapcsolatot az AGM Consulting csapatával."
          />
        )}

        {/* No company selected (multiple companies) */}
        {hasCompanies && !selectedCompanyId && companies.length > 1 && (
          <div className="space-y-4">
            <p className="text-sm text-[#64748B]">Válasszon céget a folytatáshoz:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {companies.map(co => (
                <button
                  key={co.id}
                  data-testid={`company-card-${co.id}`}
                  onClick={() => setSelectedCompanyId(co.id)}
                  className="text-left bg-white border border-[#E2E8F0] rounded-lg p-6 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#C5A059]/30 transition-all duration-300"
                >
                  <Building2 size={24} className="text-[#0B1B3D] mb-3" />
                  <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">{co.company_name}</h3>
                  {co.novitax_enabled && (
                    <span className="inline-block mt-2 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">Novitax aktív</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Company selected — show dashboard */}
        {hasCompanies && selectedCompanyId && (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white border border-[#E2E8F0] p-1 rounded-lg">
              <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
                <LayoutDashboard size={16} className="mr-2" /> Áttekintés
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
                <FileText size={16} className="mr-2" /> Dokumentumok
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
                <BarChart3 size={16} className="mr-2" /> Kimutatások
              </TabsTrigger>
              <TabsTrigger value="policies" data-testid="tab-policies" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
                <Shield size={16} className="mr-2" /> Szabályzatok
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">ÁFA egyenleg</span>
                    <div className="w-8 h-8 bg-[#C5A059]/10 rounded-lg flex items-center justify-center">
                      <BarChart3 size={16} className="text-[#C5A059]" />
                    </div>
                  </div>
                  <p className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">— Ft</p>
                  <p className="text-xs text-[#64748B] mt-1">Novitax integráció előkészítve</p>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Adó egyenleg</span>
                    <div className="w-8 h-8 bg-[#0B1B3D]/5 rounded-lg flex items-center justify-center">
                      <BarChart3 size={16} className="text-[#0B1B3D]" />
                    </div>
                  </div>
                  <p className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">— Ft</p>
                  <p className="text-xs text-[#64748B] mt-1">Adóegyenleg</p>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Profil</span>
                    <div className="w-8 h-8 bg-[#0B1B3D]/5 rounded-lg flex items-center justify-center">
                      <User size={16} className="text-[#0B1B3D]" />
                    </div>
                  </div>
                  <p className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">{user.name}</p>
                  <p className="text-xs text-[#64748B] mt-1">{user.email}</p>
                </div>
              </div>

              {/* Novitax status */}
              <div className="mt-6 bg-[#0B1B3D] rounded-lg p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-3 h-3 rounded-full ${companyDetail?.novitax_ready ? 'bg-green-400 animate-pulse' : 'bg-[#64748B]'}`} />
                  <span className="text-white font-['Outfit'] font-semibold">Novitax rendszer</span>
                </div>
                {companyDetail?.novitax_ready ? (
                  <p className="text-[#94A3B8] text-sm">
                    A Novitax kapcsolat konfigurálva ehhez a céghez. Az adatlekérdezés a Novitax API endpoint-ok véglegesítése után lesz elérhető.
                  </p>
                ) : companyDetail?.novitax_enabled ? (
                  <p className="text-[#94A3B8] text-sm">
                    A Novitax hozzáférés engedélyezve, de a konfiguráció még nem teljes. Kérjük forduljon az AGM Consulting csapatához.
                  </p>
                ) : (
                  <p className="text-[#94A3B8] text-sm">
                    A Novitax hozzáférés nincs engedélyezve ehhez a céghez. Az aktiválásról érdeklődjön az AGM Consulting csapatánál.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="documents">
              <ClientDocuments companyId={selectedCompanyId} />
            </TabsContent>

            <TabsContent value="reports">
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center">
                <BarChart3 size={40} className="text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Kimutatások</h3>
                <p className="text-sm text-[#64748B] mt-2">A pénzügyi kimutatások a Novitax integráció véglegesítése után lesznek elérhetők.</p>
              </div>
            </TabsContent>

            <TabsContent value="policies">
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center">
                <Shield size={40} className="text-[#E2E8F0] mx-auto mb-4" />
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Szabályzatok</h3>
                <p className="text-sm text-[#64748B] mt-2">A szabályzatok hamarosan elérhetők lesznek az Önhöz rendelt csomag alapján.</p>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
